import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css, keyframes } from 'styled-components';

import TippyTooltip from '@components/common/tooltip/TippyTooltip';
import { Pill, PillGroup } from '@components/course/taskEstimator/TaskEstimator.styles';
import { CustomDurationPop, CustomPagesPop } from '@components/course/taskEstimator/EstimatorControls';
import { pagesToMinutes, parsePagesInput } from '@components/course/taskEstimator/utils';
import { useTaskEstimate } from '@components/course/taskEstimator/useTaskEstimate';
import { formatPlanningMinutes, TASK_ESTIMATE_OPTIONS, toPageRanges } from '@utils/taskPlanning';
import { categoryTimeApi } from '@api/categoryTimeApi';
import { categoryMinutesOf, findCourseCategory } from '@utils/categoryTime';
import { setCategoryMinutes } from '@state/course/actions';
import { getAllCourses } from '@state/calendar/selectors';
import { getId as getSemesterId } from '@state/semester/selectors';
import { RootState } from '@state/types';
import FirstEstimateBox from './FirstEstimateBox';
import { MiniText } from '@utils/typography';
import colors from '@utils/colors';
import { PageRange } from 'shovel-lib/types';
import ManagePageRanges from '@components/task/manage/partials/ManagePageRanges';
import PlannedTag from './PlannedTag';
import {
  EstimatePopupLabel,
  EstimatePopupPanel,
  estimatePopupAppendTo,
  POP_OUT_MS,
  useFocusFieldOnOpen
} from './EstimatePopup';

type Props = {
  taskId: number;
  /** Named at the top of the popup, which covers the card it opens from (Petr, 2026-10-08). */
  title?: string;
  /** The task's course colour, which outlines the popup. */
  color?: string;
  /** Opens the task, as clicking its card does: the name at the top of the popup does it too (Petr, 2026-10-08). */
  onOpenTask?: (event: React.MouseEvent | React.KeyboardEvent) => void;
  /** Readings are estimated in pages at their source's rate, never in minutes. */
  isReading: boolean;
  /** The card is draggable, and a drag starting inside these controls is never meant as a drag. */
  onOpenChange?: (open: boolean) => void;
  /** For surfaces holding their own copy of the task, rather than reading it from the store. */
  onSaved?: () => void;
  /** The card's own time tag for an estimate, shown in place of the button as soon as one is picked. */
  renderEstimate?: (minutes: number) => React.ReactNode;
  /**
   * The footer's Add time: the same popup, adding what is picked to the time still needed rather than setting it. A
   * reading's adds pages instead ("+pages"), so its time keeps coming from its pages: minutes added to a reading made
   * its card say more than its pages did (Petr, 2026-10-22).
   */
  addTime?: boolean;
  /**
   * The task's category, for a first estimate (Petr, 2026-10-29): while the category has no default and other tasks of
   * it have no time either, "Also for 12 other Homework" (ticked) gives them the pick too and makes it the default.
   */
  category?: { id: number; courseId: number; name: string };
};

// What was picked, in the button's place until the card shows it: minutes, or pages the server turns into minutes.
type Picked = { minutes: number } | { pages: number };

/**
 * A task with no estimate cannot be auto-planned, so on the pile that state offers the fix rather than
 * only reporting it. The controls are the estimator's own, the same ones the Plan Week dialog puts in
 * front of its unestimated tasks.
 */

const PileEstimateControl = ({
  taskId,
  title,
  color,
  isReading,
  onOpenTask,
  onOpenChange,
  onSaved,
  renderEstimate,
  addTime,
  category
}: Props) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [closing, setClosing] = useState(false);
  const [picked, setPicked] = useState<Picked | null>(null);
  // A reading's pace, fetched as its popup opens, so the pages it is given show as time at once.
  const [pace, setPace] = useState<number | undefined>(undefined);
  const mounted = useRef(true);
  // A reading's ranges, fetched as its +pages popup opens, so it shows the pages it has before more are added.
  const [ranges, setRanges] = useState<PageRange[] | undefined>(undefined);
  const { saveEstimate, saveEstimatePages, addPages, readingPageRanges, readingPace } = useTaskEstimate();
  const addsPages = isReading && !!addTime;
  const dispatch = useDispatch();
  const semesterId = useSelector(getSemesterId);
  // Whether the category has a default yet, from the course as the store has it (a default set meanwhile counts).
  const categoryHasDefault = useSelector(
    (state: RootState) =>
      !!category && categoryMinutesOf(findCourseCategory(getAllCourses(state), category.courseId, category.id)) != null
  );
  // The category's other tasks with no time yet, fetched as the popup opens; null until then, 0 when there are none.
  const [othersUnestimated, setOthersUnestimated] = useState<number | null>(null);
  const [alsoForOthers, setAlsoForOthers] = useState(true);
  const offersOthers = !!category && !isReading && !addTime && !categoryHasDefault;
  const showsOthers = offersOthers && !!othersUnestimated;

  // Set on mount as well as cleared on unmount: a code reload re-runs effects, and a flag only ever cleared stayed
  // false, so the popup never finished closing after a pick.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Whether it is open now, for the tooltip's onHidden, which runs after this render's `open` has moved on.
  const openRef = useRef(false);
  // Whether the card was last told it is open, and so still has to hear that it closed.
  const toldOpen = useRef(false);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const tellOpen = (next: boolean) => {
    if (toldOpen.current === next) return;
    toldOpen.current = next;
    onOpenChangeRef.current?.(next);
  };

  // Gone before the tooltip said it had hidden (a pick swaps Estimate for the card's time tag): the card would keep
  // treating the popup as open, and so keep itself from being dragged.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => tellOpen(false), []);

  // Closing is only passed on once the tooltip has faded out (onHidden). The card's footer, which +time and +pages sit
  // in, hides as soon as it hears, and the popup still fading slid after its vanished button to the screen's corner
  // (Petr, 2026-10-22: "it should just close").
  const setOpenState = (next: boolean) => {
    setOpen(next);
    openRef.current = next;
    if (next) tellOpen(true);
    // It stays shrunk once it has gone, so its entrance doesn't play again as the tooltip fades it out.
    if (next) setClosing(false);
    if (!next) setFailed(false);
  };

  useFocusFieldOnOpen(open, panelRef);

  useEffect(() => {
    if (!open || !offersOthers || !semesterId || !category) return;
    setAlsoForOthers(true);
    categoryTimeApi.unfinishedCounts(semesterId, category.courseId, true).then(response => {
      // This task has no time yet either, so it's in the count.
      if (mounted.current && response.ok && response.data) {
        setOthersUnestimated(Math.max((response.data[category.id] || 0) - 1, 0));
      }
    });
    // Once per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offersOthers]);

  useEffect(() => {
    // Added pages have no tag to stand in for, so they need no pace.
    if (!open || !isReading || addTime || pace !== undefined) return;
    readingPace(taskId).then(found => {
      if (mounted.current && found) setPace(found);
    });
    // Once per popup: the pace stays what it was for this card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isReading]);

  useEffect(() => {
    if (!open || !addsPages || ranges !== undefined) return;
    readingPageRanges(taskId).then(found => {
      if (mounted.current && found) setRanges(found);
    });
    // Once per opening: a save clears them, so the next one shows the pages just added.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, addsPages, ranges]);

  // What stands in the button's place once something is picked: the card's own tag when the time is known, which for
  // pages is as soon as the reading's pace is (Petr: the pages showed first and then turned into time).
  const pickedMinutes = picked && ('minutes' in picked ? picked.minutes : pace && pagesToMinutes(picked.pages, pace));

  // The card underneath opens the task, and it lets through anything that has not already been handled.
  const handled = (event: React.MouseEvent) => event.preventDefault();

  // The popup closes as soon as something is picked, shrinking back into its button, which then becomes the card's time
  // tag for the pick while it saves. The save reads and writes the task, over a second locally: the popup used to sit
  // there unchanged meanwhile and then vanish, and the picked value used to stand in the button before the tag came
  // (Petr: "not smooth once I set a value"). With minutes the tag is the one the card shows once saved, so nothing moves
  // when the save is through. A failed save opens the popup again to say so.
  const save = async (pick: Picked, run: () => Promise<boolean>) => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    setClosing(true);
    const saving = run();
    await new Promise(resolve => setTimeout(resolve, POP_OUT_MS));
    if (mounted.current) {
      // Added time has no tag to stand in for: the card's own tag changes once the save is through.
      if (!addTime) setPicked(pick);
      setOpenState(false);
    }
    const saved = await saving;
    if (saved) onSaved?.();
    // Saved, the card has usually drawn the estimate in this control's place already.
    if (!mounted.current) return;
    setBusy(false);
    setPicked(null);
    if (saved) setRanges(undefined);
    if (!saved) {
      setOpenState(true);
      setFailed(true);
    }
  };

  // The popup closes as the task opens, so it isn't left standing over the task's dialog.
  const openTask = (event: React.MouseEvent | React.KeyboardEvent) => {
    setOpenState(false);
    onOpenTask?.(event);
  };

  const onMinutes = (minutes: number) => {
    const forOthers = showsOthers && alsoForOthers;
    return save({ minutes }, async () => {
      const saved = await saveEstimate(taskId, minutes, addTime);
      // The task first, then the category: its default, and the same time for its other tasks with none.
      if (saved && forOthers && category) {
        dispatch(
          setCategoryMinutes.request({
            courseId: category.courseId,
            categoryId: category.id,
            minutes,
            applyToUnfinished: true,
            onlyUnestimated: true
          })
        );
      }
      return saved;
    });
  };

  // Returns false so the pages field can say what it could not read. The estimator's pop owns that message.
  const onPages = (raw: string) => {
    const parsed = parsePagesInput(raw);
    if (!parsed) return false;
    void save({ pages: parsed.pages }, () =>
      addTime ? addPages(taskId, parsed, ranges) : saveEstimatePages(taskId, toPageRanges(parsed))
    );
    return true;
  };

  const panel = (
    <EstimatePopupPanel ref={panelRef} $closing={closing} $color={color} onClick={handled}>
      {title &&
        (onOpenTask ? (
          <TaskTitle
            title={title}
            $link
            role="button"
            tabIndex={0}
            onClick={openTask}
            onKeyDown={event => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              openTask(event);
            }}
          >
            {title}
          </TaskTitle>
        ) : (
          <TaskTitle title={title}>{title}</TaskTitle>
        ))}
      <EstimatePopupLabel>
        {isReading
          ? addTime
            ? '📖 How many more pages?'
            : '📖 How many pages is it?'
          : addTime
          ? '⏱️ How much more time?'
          : '⏱️ How long will it take?'}
      </EstimatePopupLabel>
      {showsOthers && category && (
        // Right under the question, as in Course details' category popup.
        <FirstEstimateBox
          others={othersUnestimated!}
          categoryName={category.name}
          checked={alsoForOthers}
          onToggle={() => setAlsoForOthers(value => !value)}
        />
      )}
      {addsPages && ranges && ranges.length > 0 && (
        // The task popup's own chips, without their ×: what is typed below goes in after them.
        <ManagePageRanges pageRanges={ranges} noInput readOnly small />
      )}
      {isReading ? (
        // Only the field, as in Auto-plan's Not estimated list: a student counts a chapter's pages, not picks them.
        <CustomPagesPop value="" bare onApply={onPages} />
      ) : (
        // A preset, or any other time typed in the field under them. No Custom button to open that field: it is always
        // there (Petr: "it's either the fast select or the input").
        <>
          <PillGroup>
            {TASK_ESTIMATE_OPTIONS.map(minutes => (
              <Pill key={minutes} type="button" disabled={busy} onClick={() => void onMinutes(minutes)}>
                {formatPlanningMinutes(minutes)}
              </Pill>
            ))}
          </PillGroup>
          <CustomDurationPop value={null} onApply={minutes => void onMinutes(minutes)} />
        </>
      )}
      {failed && <SaveError>Couldn't save that. Try again.</SaveError>}
    </EstimatePopupPanel>
  );

  const toggle = (event: React.MouseEvent) => {
    handled(event);
    setOpenState(!open);
  };

  return (
    <TippyTooltip
      target={
        // One anchor for the popup, whether it holds the button or the tag that takes its place.
        <Slot>
          {picked ? (
            <PickedTag>
              {pickedMinutes && renderEstimate ? (
                renderEstimate(pickedMinutes)
              ) : (
                <>
                  <PlannedTag timeNeed={0} timePlanned={0} isTaskCard bare font={10} noTooltip />
                  <PendingPages>
                    {'pages' in picked ? `${picked.pages} pages` : formatPlanningMinutes(picked.minutes)}
                  </PendingPages>
                </>
              )}
            </PickedTag>
          ) : (
            // Add time's button is the same thin pill as Estimate (Petr, 2026-10-09).
            <TriggerPill type="button" $selected={open} disabled={busy} onClick={toggle}>
              {addTime ? (isReading ? '+pages' : '+time') : isReading ? 'Set pages' : 'Estimate'}
            </TriggerPill>
          )}
        </Slot>
      }
      content={panel}
      position={'top-end'}
      interactive
      controlled
      visible={open}
      onClickOutside={() => setOpenState(false)}
      onHidden={() => {
        if (!openRef.current) tellOpen(false);
      }}
      className="estimate-popup"
      lightTheme
      noPadding
      arrow={false}
      width={'none'}
      appendTo={estimatePopupAppendTo}
      // Above the card's hover footer, which sits at 100.
      zIndex={200}
    />
  );
};

export default PileEstimateControl;

const TriggerPill = styled(Pill)`
  font-size: 10px;
  padding: 2px 8px;
  white-space: nowrap;
`;

// Laid out as the card's own tag is, so the tag shown here and the card's take the very same place.
const Slot = styled.div`
  display: flex;
  align-items: center;
`;

// The tag pops into the button's place as the popup finishes shrinking into it.
const tagIn = keyframes`
  from {
    transform: scale(0.6);
    opacity: 0;
  }
  70% {
    transform: scale(1.08);
    opacity: 1;
  }
  to {
    transform: none;
  }
`;

const PickedTag = styled.div`
  display: flex;
  align-items: center;
  transform-origin: 100% 50%;
  animation: ${tagIn} 260ms cubic-bezier(0.3, 0.7, 0.4, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Without the reading's pace yet (or anywhere the card's tag isn't given), the tag says what was picked, gently
// pulsing, until the card shows the time.
const savingPulse = keyframes`
  50% {
    opacity: 0.5;
  }
`;

const PendingPages = styled(MiniText)`
  font-size: 10px;
  color: ${colors.negative};
  animation: ${savingPulse} 1s ease-in-out infinite;
`;

// The popup opens over the card's own title, so it names the task itself: up to two lines, as wide as the presets
// under it and no wider. Small and grey, so the question under it leads (Petr, 2026-10-08). Where the card opens its
// task, so does the name, turning purple as it is pointed at.
const TaskTitle = styled.span<{ $link?: boolean }>`
  display: -webkit-box;
  width: 0;
  min-width: 100%;
  margin-bottom: -4px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  font-family: 'Quicksand', sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  color: ${colors.gray};
  word-break: break-word;

  ${({ $link, theme }) =>
    $link &&
    css`
      cursor: pointer;
      transition: color 0.12s ease;

      &:hover,
      &:focus-visible {
        color: ${theme.primary};
      }
    `}
`;

const SaveError = styled(MiniText)`
  color: ${colors.negative};
`;
